import type { LatLng } from '@/state/types';
import { haversineKm } from '@/lib/geo';

/**
 * Phase 6.3 — Hydrant data layer.
 *
 * Strategy:
 *   1. Try Overpass API for OpenStreetMap fire-hydrant nodes around the city.
 *   2. Cache the result in localStorage under `hydrants_<lat>_<lng>_<radiusKm>`.
 *   3. On Overpass failure (network / timeout / quota), generate a synthetic
 *      grid of hydrants spread roughly 0.5 km apart with random jitter, so
 *      the gameplay water-supply mechanic still works offline.
 */

export interface Hydrant {
  id: string;
  position: LatLng;
  /** Either "osm" for real OSM data or "synthetic" for the grid fallback. */
  source: 'osm' | 'synthetic';
}

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';

/** localStorage key for a city/radius combo. */
function cacheKey(center: LatLng, radiusKm: number): string {
  return `hydrants_${center.lat.toFixed(3)}_${center.lng.toFixed(3)}_${radiusKm}`;
}

interface OverpassNode {
  type: 'node';
  id: number;
  lat: number;
  lon: number;
}
interface OverpassResponse {
  elements: OverpassNode[];
}

/**
 * Fetch hydrants from Overpass API. Returns null on any failure (caller falls
 * back to synthetic data).
 */
export async function fetchHydrantsFromOverpass(
  center: LatLng,
  radiusKm: number,
  signal?: AbortSignal,
): Promise<Hydrant[] | null> {
  const radiusM = Math.round(radiusKm * 1000);
  const query = `[out:json][timeout:25];
(
  node["emergency"="fire_hydrant"](around:${radiusM},${center.lat},${center.lng});
);
out body;`;
  try {
    const resp = await fetch(OVERPASS_URL, {
      method: 'POST',
      body: 'data=' + encodeURIComponent(query),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      signal,
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as OverpassResponse;
    if (!Array.isArray(data.elements)) return null;
    return data.elements.map((n) => ({
      id: `osm_${n.id}`,
      position: { lat: n.lat, lng: n.lon },
      source: 'osm' as const,
    }));
  } catch {
    return null;
  }
}

/**
 * Synthetic grid fallback. Roughly 1 hydrant per 0.5 km² (so ~4 per km²).
 * Grid is jittered ±50 m so the visual isn't an obvious checkerboard.
 */
export function generateFallbackHydrants(
  center: LatLng,
  radiusKm: number,
  rng: () => number = Math.random,
): Hydrant[] {
  // Grid spacing — tighter than full radius to keep coverage usable.
  const spacingKm = 0.5;
  const cellsPerSide = Math.ceil((radiusKm * 2) / spacingKm);
  const out: Hydrant[] = [];
  // 1° lat ≈ 111.32 km; 1° lng ≈ 111.32 × cos(lat).
  const cosLat = Math.cos((center.lat * Math.PI) / 180);
  for (let i = 0; i < cellsPerSide; i++) {
    for (let j = 0; j < cellsPerSide; j++) {
      const dxKm = (i - cellsPerSide / 2 + 0.5) * spacingKm;
      const dyKm = (j - cellsPerSide / 2 + 0.5) * spacingKm;
      // Jitter ±50 m (0.05 km)
      const jitterX = (rng() - 0.5) * 0.1;
      const jitterY = (rng() - 0.5) * 0.1;
      const dLat = (dyKm + jitterY) / 111.32;
      const dLng = (dxKm + jitterX) / (111.32 * cosLat);
      const lat = center.lat + dLat;
      const lng = center.lng + dLng;
      // Drop points outside the radius (round bounding)
      if (haversineKm(center, { lat, lng }) > radiusKm) continue;
      out.push({
        id: `syn_${i}_${j}`,
        position: { lat, lng },
        source: 'synthetic',
      });
    }
  }
  return out;
}

/**
 * Public top-level loader. Returns cached → live → fallback in that order.
 * Persists to localStorage on every successful Overpass fetch.
 */
export async function loadHydrants(
  center: LatLng,
  radiusKm: number,
  signal?: AbortSignal,
): Promise<Hydrant[]> {
  const key = cacheKey(center, radiusKm);

  // 1. Cache check
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as Hydrant[];
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      // bad cache entry — ignore and continue
    }
  }

  // 2. Live fetch
  const live = await fetchHydrantsFromOverpass(center, radiusKm, signal);
  if (live && live.length > 0) {
    persist(key, live);
    return live;
  }

  // 3. Fallback
  const synth = generateFallbackHydrants(center, radiusKm);
  persist(key, synth);
  return synth;
}

function persist(key: string, hydrants: Hydrant[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(hydrants));
  } catch {
    // quota or disabled — non-fatal
  }
}

/** Find the nearest hydrant within `maxDistanceM` meters of `from`. */
export function findNearestHydrantInRange(
  hydrants: Hydrant[],
  from: LatLng,
  maxDistanceM: number,
): Hydrant | null {
  let best: Hydrant | null = null;
  let bestDistKm = Infinity;
  const maxKm = maxDistanceM / 1000;
  for (const h of hydrants) {
    const d = haversineKm(from, h.position);
    if (d > maxKm) continue;
    if (d < bestDistKm) {
      best = h;
      bestDistKm = d;
    }
  }
  return best;
}
